import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { enrichFunnelPercents, fmtAnalyticsCount, fmtCount, fmtDurationSeconds, getVoiceFallbackClient } from './_shared';
import { fetchVoiceAgents, mapVoiceAgent } from './voiceAgents';
import type { VoiceAgentRow } from './voiceAgents';
import type { CallAnalyticsCampaignTableRow, CallAnalyticsFunnelStep } from './callAnalytics';
import { fetchConversationsPage } from './outboundScheduler';

// ── Analytics (individual endpoints) ────────────────────────────────

/** `GET /api/analytics/funnel` — five-step conversion funnel. */
export async function fetchAnalyticsFunnel(
  axiosInstance?: AxiosInstance,
  params?: { start_date?: string; end_date?: string; campaign_id?: string; days?: number }
): Promise<CallAnalyticsFunnelStep[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/funnel`;
  const response = await client.get<any>(url, params ? { params } : undefined);
  const raw = response.data;
  let steps: any[] = [];
  if (Array.isArray(raw)) steps = raw;
  else if (raw && typeof raw === 'object') {
    const direct = (raw as any).steps ?? (raw as any).funnel;
    if (Array.isArray(direct)) steps = direct;
    else if ((raw as any).funnel && typeof (raw as any).funnel === 'object' && Array.isArray((raw as any).funnel.funnel)) steps = (raw as any).funnel.funnel;
  }
  const mapped = steps.map((s: any) => ({
    label: String(s.label ?? s.stage ?? s.step ?? ''),
    value: fmtAnalyticsCount(s.value ?? s.count),
    percent: Number(s.percent ?? s.pct ?? 0),
  }));
  return enrichFunnelPercents(mapped);
}

export type AnalyticsReactivationPayload = {
  period_days: number;
  campaigns_included: number;
  contacts_dialed: number;
  contacts_reached: number;
  re_engagement_rate: number;
  appointments_booked: number;
  estimated_revenue_recovered: number | null;
  cost_per_reactivation: number | null;
};

/** `GET /api/analytics/reactivation` — database-reactivation KPIs (BOOSTT-3173). */
export async function fetchAnalyticsReactivation(
  axiosInstance?: AxiosInstance,
  params?: { days?: number; campaign_id?: string; segment_id?: string }
): Promise<AnalyticsReactivationPayload> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/reactivation`;
  const response = await client.get<AnalyticsReactivationPayload>(url, params ? { params } : undefined);
  return response.data;
}

/**
 * `GET /api/analytics/campaigns` — campaign performance comparison. Also
 * surfaces the real `total` count the backend computes, for real pagination —
 * same reasoning as fetchConversationsPage/fetchAnalyticsAgents.
 */
export async function fetchAnalyticsCampaigns(
  axiosInstance?: AxiosInstance,
  params?: { start_date?: string; end_date?: string; campaign_id?: string; days?: number; limit?: number; offset?: number }
): Promise<{ campaigns: CallAnalyticsCampaignTableRow[]; total: number }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/campaigns`;
  const response = await client.get<any>(url, params ? { params } : undefined);
  const rd = response.data;
  let rows: any[] = [];
  if (Array.isArray(rd)) rows = rd;
  else if (rd && typeof rd === 'object') {
    if (Array.isArray(rd.campaigns)) rows = rd.campaigns;
    else if (Array.isArray(rd.campaigns?.campaigns)) rows = rd.campaigns.campaigns;
  }
  const total = Array.isArray(rd) ? rows.length : Number(rd?.total ?? rows.length);
  const campaigns = rows.map((c: any) => {
    const sess = c.sessions ?? c.session_count ?? c.total_sessions ?? 0;
    const comp = c.completed ?? c.completed_sessions ?? 0;
    const compRate = c.completed_rate ?? c.completion_rate ?? (sess > 0 ? (comp / sess) * 100 : 0);
    const booked = c.booked ?? c.total_booked ?? c.appointments ?? c.total_appointments ?? 0;
    const rate = c.rate ?? c.book_rate ?? c.booking_rate ?? (sess > 0 ? (booked / sess) * 100 : 0);

    return {
      id: String(c.campaign_id ?? c.id ?? ''),
      name: String(c.name ?? '—'),
      icon: String(c.icon ?? 'solar:chart-square-bold'),
      status: String(c.status ?? (Number(sess) > 0 ? 'Live' : '—')),
      scans: fmtCount(c.scans ?? c.scan_count ?? c.total_scans),
      sessions: fmtCount(sess),
      completed: String(comp),
      completedRate: `${Number(compRate).toFixed(0)}%`,
      booked: String(booked),
      rate: `${Number(rate).toFixed(0)}%`,
      avgDuration: fmtDurationSeconds(c.avg_duration_seconds ?? c.avg_duration),
      avgSentiment: c.avg_sentiment != null ? Number(c.avg_sentiment).toFixed(2) : (c.sentiment ?? '—'),
      rev: String(c.rev ?? c.revenue ?? '—'),
      cpa: String(c.cpa ?? c.cost_per_appt ?? '—'),
      color: (c.color as CallAnalyticsCampaignTableRow['color']) || 'success.main',
    };
  });
  return { campaigns, total };
}

/**
 * `GET /api/analytics/agents` — the "By Agent" tab used to call fetchVoiceAgents
 * (the plain agent CRUD list, with no session data at all) since this endpoint
 * never existed, so every metric always showed 0. Reuses mapVoiceAgent, whose
 * `raw` passthrough already matches the field names getAgentMetric looks for
 * (campaigns, total_sessions, completed, booked, booking_rate). Also surfaces
 * the real `total` count the backend computes, for real pagination — same
 * reasoning as fetchConversationsPage for the Sessions tab.
 */
export async function fetchAnalyticsAgents(
  axiosInstance?: AxiosInstance,
  params?: { days?: number; limit?: number; offset?: number }
): Promise<{ agents: VoiceAgentRow[]; total: number }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/agents`;
  const response = await client.get<any>(url, params ? { params } : undefined);
  const rd = response.data;
  const rows: any[] = Array.isArray(rd) ? rd : Array.isArray(rd?.agents) ? rd.agents : [];
  const total = Array.isArray(rd) ? rows.length : Number(rd?.total ?? rows.length);
  return { agents: rows.map(mapVoiceAgent), total };
}

export type AnalyticsTrendPoint = { date: string; sessions: number; bookings: number };

/** `GET /api/analytics/trends` — daily aggregate trend data. */
export async function fetchAnalyticsTrends(
  axiosInstance?: AxiosInstance,
  params?: { start_date?: string; end_date?: string; campaign_id?: string; days?: number }
): Promise<AnalyticsTrendPoint[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/trends`;
  const response = await client.get<any>(url, params ? { params } : undefined);
  const rows = Array.isArray(response.data) ? response.data : response.data?.trends ?? [];
  return rows.map((r: any) => ({
    date: String(r.date ?? r.day ?? ''),
    sessions: Number(r.sessions ?? r.session_count ?? 0),
    bookings: Number(r.bookings ?? r.booking_count ?? 0),
  }));
}

export type AnalyticsEvent = { event_type: string; occurred_at: string; [key: string]: unknown };

/** `GET /api/analytics/events/{session_id}` — session events ordered chronologically. */
export async function fetchSessionEvents(sessionId: string, axiosInstance?: AxiosInstance): Promise<AnalyticsEvent[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/events/${sessionId}`;
  const response = await client.get<any>(url);
  return Array.isArray(response.data) ? response.data : response.data?.events ?? [];
}

export type AnalyticsSignalsPayload = {
  period_days: number;
  total_signals: number;
  signal_types: Record<string, number>;
  avg_confidence: Record<string, number>;
  top_intents: { intent: string; count: number }[];
  top_objections: { objection: string; count: number }[];
  objection_resolution_rate: number;
  sentiment: {
    average: number;
    trajectory: { improving: number; stable: number; declining: number };
    sample_size: number;
  };
  high_intent_signals: number;
};

/** `GET /api/analytics/signals` — intent/sentiment/objection breakdown. */
export async function fetchAnalyticsSignals(
  axiosInstance?: AxiosInstance,
  params?: { start_date?: string; end_date?: string; campaign_id?: string; days?: number }
): Promise<AnalyticsSignalsPayload | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/signals`;
  const response = await client.get<any>(url, params ? { params } : undefined);
  return response.data || null;
}

export type AnalyticsAttributionRow = { creative_id: string; scans: number; sessions: number; bookings: number };

export type AnalyticsAttributionPayload = {
  period_days: number;
  creatives: AnalyticsAttributionRow[];
  token_stats: {
    total_generated: number;
    total_scanned: number;
    scan_rate: number;
  };
};

/** `GET /api/analytics/attribution` — creative attribution analytics. */
export async function fetchAnalyticsAttribution(
  axiosInstance?: AxiosInstance,
  params?: { start_date?: string; end_date?: string; campaign_id?: string; days?: number }
): Promise<AnalyticsAttributionPayload | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/attribution`;
  const response = await client.get<any>(url, params ? { params } : undefined);
  return response.data || null;
}

/** `GET /api/analytics/export/contacts` — export contacts as CSV Blob. */
export async function exportContacts(axiosInstance?: AxiosInstance): Promise<Blob> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/export/contacts`;
  const response = await client.get<Blob>(url, { responseType: 'blob' });
  return response.data;
}

/** `GET /api/analytics/export/appointments` — export appointments as CSV Blob. */
export async function exportAppointments(axiosInstance?: AxiosInstance): Promise<Blob> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/export/appointments`;
  const response = await client.get<Blob>(url, { responseType: 'blob' });
  return response.data;
}

/** `GET /api/analytics/export/sessions` — export sessions as CSV Blob. */
export async function exportSessions(
  axiosInstance?: AxiosInstance,
  params?: { start_date?: string; end_date?: string; campaign_id?: string; days?: number }
): Promise<Blob> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/export/sessions`;
  const response = await client.get<Blob>(url, { responseType: 'blob', params });
  return response.data;
}
